import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { message, notification } from 'antd';
import { stringify } from 'qs';
import { getBrowserInfo, getToken } from './utils';
import browserHistory from '../config/browserHistory';
import ShowError from './ShowError';
import reduxStore from '../redux';

axios.defaults.baseURL = process.env.REACT_APP_REQUEST_URL;
// 请求完成后的拦截器
axios.interceptors.response.use(
  (res) => {
    if (res.config.responseType === 'blob') {
      return res;
    }
    return res.data;
  },
  (err) => {
    checkStatus(err.response);
    return Promise.reject(err);
  },
);

// 创建axios实例
const service = axios.create({
  timeout: 60000,
  paramsSerializer(params) {
    // 后端使用requestParam时, 能够接收list
    return stringify(params, { arrayFormat: 'repeat' });
  },
});

export interface Page {
  list: any[];
  current: number;
  pageSize: number;
  total: number;
}

export enum ErrorShowType {
  SILENT = 0, // 不提示错误
  WARN_MESSAGE = 1, // 警告信息提示
  ERROR_MESSAGE = 2, // 错误信息提示
  NOTIFICATION = 4, // 通知提示
  REDIRECT = 9, // 页面跳转
}

export interface ResultType {
  success: boolean;
  code: number;
  msg: string;
  data?: Page | any;
  showType?: ErrorShowType;
}

// 请求时的拦截器
service.interceptors.request.use(
  (config) => {
    let headers = {};

    const token = getToken();
    if (token) {
      headers = { Authorization: `Bearer ${token}` };
    }

    config.headers = { ...config.headers, ...headers };
    return config;
  },
  (err) => {
    console.error('request err:', err);
    message.error('请求错误');
    return Promise.reject(err);
  },
);
// 请求完成后的拦截器
service.interceptors.response.use(
  (res) => {
    if (res.config.responseType === 'blob') {
      return res;
    }
    return res.data;
  },
  (err) => {
    checkStatus(err.response);
    return Promise.reject(err);
  },
);

const codeMessage = {
  0: '网络异常',
  200: '请求已成功',
  201: '新建或修改数据成功',
  202: '请求已接受,已经进入后台排队(异步任务)',
  204: '请求处理成功,没有返回内容',
  400: '客户端请求错误,服务器没有新建或修改数据',
  401: '用户未通过验证,请登录',
  403: '用户没有权限,访问被禁止',
  404: '请求地址不存在',
  406: '请求的格式不可得',
  410: '请求的资源被永久删除',
  422: '当创建一个对象时，发生一个验证错误',
  500: '服务器发生错误,无法完成请求,请检查服务器',
  502: '网关错误',
  503: '服务暂时不可用，服务器过载或正在维护',
  504: '网关超时',
};

function checkStatus(response: AxiosResponse) {
  if (!response || !response.status) {
    notification.error({
      message: '网络异常',
      description: '请检查网络连接情况',
    });
    return;
  }

  if (response.status === 401) {
    reduxStore.dispatch.user.setToken('');
    let loginPath = '/loginOnlineMall';
    if (window.location.pathname.indexOf('/login') < 0) {
      loginPath = loginPath + `?rf=${window.location.href}`;
    }
    browserHistory.push(loginPath);
    // return reduxStore.dispatch.login.setVisible();
    //401不弹提示信息直接跳转到登录页
  } else {
    const errorText = response.data.msg || response.data.message || codeMessage[response.status];
    notification.error({
      message: `错误状态 ${response.status}: ${response.statusText}`,
      description: errorText || '请联系系统管理员',
    });
  }
}

function checkError(result: ResultType): Promise<ResultType> {
  if (result == null) return Promise.reject({ msg: 'result is null or undefined' });

  if (result.success || result.code > 0) {
    return Promise.resolve(result);
  }

  ShowError(result);
  return Promise.reject(result);
}

function checkSuccessAndError(result: ResultType): Promise<ResultType> {
  if (result == null) return Promise.reject({ msg: 'result is null or undefined' });

  if (result.success || result.code > 0) {
    message.success(result.msg);
    return Promise.resolve(result);
  }
  ShowError(result);
  return Promise.reject(result);
}

function checkDownloadError(blob: Blob): Promise<any> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (res) => {
      const { result } = res.target as FileReader; // 得到字符串
      const data = JSON.parse(result as string); // 解析成json对象
      ShowError(data);
      reject(data);
    }; // 成功回调
    reader.onerror = (err) => {
      ShowError({ success: false, code: 0, msg: 'JSON解析错误' });
      reject(err);
    }; // 失败回调
    reader.readAsText(blob, 'utf-8'); // 按照utf-8编码解析
  });
}

function handleDownloadFile(response: any): Promise<any> {
  if (response.headers['content-type'].includes('json')) {
    return checkDownloadError(response.data);
  }

  if (response.headers['content-disposition'] == null) {
    return Promise.reject('文件名为空');
  }

  const browser = getBrowserInfo(); // 取到浏览器完整信息
  // 浏览器名字
  const browserName = `${browser}`.replace(/[0-9./]/gi, '').toLowerCase();
  const fileName = response.headers['content-disposition'].split('=')[1];
  const blob = new Blob([response.data]);
  if (browserName === 'chrome') {
    // 谷歌浏览器
    const downloadElement = document.createElement('a');
    const href = window.URL.createObjectURL(blob);
    downloadElement.href = href;
    downloadElement.download = decodeURI(fileName);
    document.body.appendChild(downloadElement);
    downloadElement.click();
    // IE不支持该点击事件
    document.body.removeChild(downloadElement);
    window.URL.revokeObjectURL(href);
  } else if (window.navigator.msSaveBlob) {
    // IE或者360兼容模式，不考虑其他浏览器
    window.navigator.msSaveOrOpenBlob(blob, fileName);
  }

  return Promise.resolve();
}

function get(url: string, params?: object, config?: AxiosRequestConfig): Promise<ResultType> {
  if (url.startsWith('/')) {
    // url
    return service.get(url, { params, ...config });
  }
  // routeId
  return service.get(`/gateWay/redirect?routeId=${url}`, { params, ...config });
}

function getWMessage(
  url: string,
  params?: object,
  config?: AxiosRequestConfig,
): Promise<ResultType> {
  // 添加统一的message提示
  return get(url, params, config).then(checkError);
}

function post(url: string, params: object, config?: object): Promise<ResultType> {
  if (url.startsWith('/')) {
    // url
    return service.post(url, params, config);
  }
  // routeId
  return service.post(`/gateWay/redirect?routeId=${url}`, params, config);
}

function postWMessage(
  url: string,
  params: object,
  config?: AxiosRequestConfig,
): Promise<ResultType> {
  // 添加统一的message提示
  return post(url, params, config).then(checkSuccessAndError);
}

function deleteWMessage(
  url: string,
  params: object | string[],
  config?: AxiosRequestConfig,
): Promise<ResultType> {
  if (url.startsWith('/')) {
    // url
    return service
      .delete<ResultType, ResultType>(url, { params, ...config })
      .then(checkSuccessAndError);
  }
  // routeId
  return service
    .delete<ResultType, ResultType>(`/gateWay/redirect?routeId=${url}`, { params, ...config })
    .then(checkSuccessAndError);
}

function download(url: string, params?: object, config?: AxiosRequestConfig): Promise<any> {
  return get(url, params, { responseType: 'blob', ...config }).then(handleDownloadFile);
}

export default {
  get,
  getWMessage,
  post,
  postWMessage,
  deleteWMessage,
  download,
};
